import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelectedColoc } from '../hooks/useSelectedColoc';
import { roomsService } from '../services/rooms.service';
import { colocService } from '../services/coloc.service';
import {
  RoomDetail,
  BADGE_FERMEE,
  getRoomStatusLabel,
  getRoomStatusColor,
} from '../types/room';
import { formatCurrency, formatDate } from '../utils/format';
import PhotoManager from '../components/PhotoManager';
import BathroomSharing from '../components/BathroomSharing';
import FieldPreview from '../components/FieldPreview';
import { AIDE_NUMERO, AIDE_TANTIEMES, EditTab } from './roomFields';
import { APERCUS_CHAMBRE } from './RoomsPage';
import '../styles/RoomsPage.css';
import '../styles/DetailPages.css';
import { getErrorMessage } from '../utils/error';
import { urlChambrePublique } from '../utils/publicUrl';

/**
 * La fiche d'une chambre — une PAGE, plus un panneau latéral.
 *
 * Dernier écran du back-office à porter ce motif, avec Locataires. Le détail
 * (occupant, disponibilité, équipements, financier) tenait dans 460 px et
 * masquait quatre colonnes de la liste pour y arriver.
 *
 * La modale d'édition à onglets, elle, RESTE une modale : c'est le motif retenu
 * pour le CRUD complexe de chambre (cf. skill senior-uiux-web-coloc §3.2), pas
 * un dépliage subi. Elle a simplement suivi le dossier.
 */
const RoomDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const selectedColoc = useSelectedColoc();

  const [rooms, setRooms] = useState<RoomDetail[]>([]);
  const [isRentControlled, setIsRentControlled] = useState(false);
  const [introuvable, setIntrouvable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editRoom, setEditRoom] = useState<RoomDetail | null>(null);
  const [editTab, setEditTab] = useState<EditTab>('characteristics');
  const [editName, setEditName] = useState('');
  const [editFloor, setEditFloor] = useState('');
  const [editSurface, setEditSurface] = useState('');
  const [editBedSize, setEditBedSize] = useState('');
  const [editHasFurnitures, setEditHasFurnitures] = useState(false);
  const [editBathroomShared, setEditBathroomShared] = useState('');
  const [editIsTV, setEditIsTV] = useState(false);
  const [editIsCloset, setEditIsCloset] = useState(false);
  const [editIsDesk, setEditIsDesk] = useState(false);
  const [editIsLocker, setEditIsLocker] = useState(false);
  const [editPriceNIC, setEditPriceNIC] = useState('');
  const [editRentSupplement, setEditRentSupplement] = useState('');
  const [editCharges, setEditCharges] = useState('');
  const [editDeposit, setEditDeposit] = useState('');
  const [editAgencyExpenses, setEditAgencyExpenses] = useState('');
  const [editLotNumber, setEditLotNumber] = useState('');
  const [editRoomNumber, setEditRoomNumber] = useState('');
  const [editTantiemes, setEditTantiemes] = useState('');
  const [editAvailableStarting, setEditAvailableStarting] = useState('');
  const [editIntroTxt, setEditIntroTxt] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [deleteRoom, setDeleteRoom] = useState<RoomDetail | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [fermetureLoading, setFermetureLoading] = useState(false);

  const loadData = useCallback(async () => {
    // Pas encore de colocation sélectionnée : on RESTE en chargement. Sur un
    // lien direct, `Layout` la pose juste après le montage et relance ceci.
    if (!selectedColoc?.id || !id) return;
    setLoading(true);
    try {
      const [data, details] = await Promise.all([
        roomsService.listForColoc(selectedColoc.id),
        colocService.getColocDetails(selectedColoc.id),
      ]);
      setRooms(data);
      setIsRentControlled(!!details.isRentControlled);
      setIntrouvable(!data.some((r) => r.id === id));
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du chargement de la chambre'));
    } finally {
      setLoading(false);
    }
  }, [selectedColoc, id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fermeture des modales par Échap.
  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (deleteRoom) setDeleteRoom(null);
      else if (editRoom) setEditRoom(null);
    };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [deleteRoom, editRoom]);

  // Relue dans la liste à jour : figer l'objet afficherait des montants
  // périmés juste après une modification.
  const room = rooms.find((r) => r.id === id) || null;

  const openEdit = (room: RoomDetail) => {
    setEditRoom(room);
    setEditTab('characteristics');
    setEditName(room.name || '');
    setEditFloor(String(room.floor || 0));
    setEditSurface(String(room.surface || 0));
    setEditBedSize(room.bedSize != null ? String(room.bedSize) : '');
    setEditHasFurnitures(room.hasFurnitures);
    setEditBathroomShared(String(room.bathroomSharedWith));
    setEditIsTV(room.isTV);
    setEditIsCloset(room.isCloset);
    setEditIsDesk(room.isDesk);
    setEditIsLocker(room.isLocker);
    setEditPriceNIC(String(room.priceNIC));
    setEditRentSupplement(room.rentSupplement != null ? String(room.rentSupplement) : '');
    setEditCharges(String(room.charges));
    setEditDeposit(String(room.depositAmount));
    setEditAgencyExpenses(String(room.agencyExpenses));
    setEditLotNumber(room.lotNumber || '');
    setEditRoomNumber(room.roomNumber != null ? String(room.roomNumber) : '');
    setEditTantiemes(String(room.tantiemes));
    setEditAvailableStarting(room.availableStarting ? room.availableStarting.split('T')[0] : '');
    setEditIntroTxt(room.introTxt);
    setEditError(null);
  };
  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editRoom) return;
    setEditLoading(true);
    setEditError(null);
    try {
      await roomsService.update(editRoom.id, {
        name: editName,
        floor: parseInt(editFloor),
        surface: parseFloat(editSurface),
        bedSize: editBedSize ? parseInt(editBedSize) : null,
        hasFurnitures: editHasFurnitures,
        bathroomSharedWith: parseInt(editBathroomShared),
        isTV: editIsTV,
        isCloset: editIsCloset,
        isDesk: editIsDesk,
        isLocker: editIsLocker,
        priceNIC: parseFloat(editPriceNIC),
        rentSupplement: parseFloat(editRentSupplement) || 0,
        charges: parseFloat(editCharges),
        depositAmount: parseFloat(editDeposit),
        agencyExpenses: parseFloat(editAgencyExpenses),
        lotNumber: editLotNumber || null,
        roomNumber: editRoomNumber ? parseInt(editRoomNumber) : null,
        tantiemes: parseInt(editTantiemes),
        availableStarting: editAvailableStarting ? new Date(editAvailableStarting).toISOString() : null,
        introTxt: editIntroTxt,
      });
      setEditRoom(null);
      await loadData();
    } catch (err) {
      setEditError(getErrorMessage(err, 'Erreur lors de la modification'));
    } finally {
      setEditLoading(false);
    }
  };
  const handleDelete = async () => {
    if (!deleteRoom) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await roomsService.delete(deleteRoom.id);
      setDeleteRoom(null);
      await loadData();
    } catch (err) {
      setDeleteError(getErrorMessage(err, 'Erreur lors de la suppression'));
    } finally {
      setDeleteLoading(false);
    }
  };

  // Réversible et sans effacement : pas de confirmation.
  const basculerFermeture = async (r: RoomDetail) => {
    setFermetureLoading(true);
    setError(null);
    try {
      await roomsService.setClosed(r.id, !r.isClosed);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors de la fermeture de la chambre'));
    } finally {
      setFermetureLoading(false);
    }
  };

  // --- Render ---

  if (loading) {
    return <div className="rooms-loading">Chargement...</div>;
  }

  if (!selectedColoc) {
    return <div className="rooms-empty">Sélectionnez une colocation</div>;
  }

  if (loading) return <div className="loading">Chargement...</div>;

  if (introuvable || !room) {
    return (
      <div className="rooms-page">
        <div className="socle-vide">
          <p className="socle-vide-titre">Chambre introuvable</p>
          <p className="socle-vide-aide">
            Elle a été supprimée, ou elle appartient à une autre colocation que
            celle sélectionnée.
          </p>
          <button className="btn-action btn-secondary" onClick={() => navigate('/rooms')}>
            Retour aux chambres
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rooms-page">
      <div className="rooms-header">
        <div>
          <button
            className="btn-action btn-secondary"
            style={{ marginBottom: 8 }}
            onClick={() => navigate('/rooms')}
          >
            ← Chambres
          </button>
          <h2>
            {room.name || 'Chambre'}
            <span
              className="room-status-badge"
              style={{
                backgroundColor: getRoomStatusColor(room.status),
                marginLeft: 12,
                verticalAlign: 'middle',
              }}
            >
              {getRoomStatusLabel(room.status)}
            </span>
            {room.isClosed && (
              <span
                className="room-status-badge"
                style={{ backgroundColor: BADGE_FERMEE.couleur, marginLeft: 6, verticalAlign: 'middle' }}
              >
                {BADGE_FERMEE.libelle}
              </span>
            )}
          </h2>
          <p className="access-hint" style={{ margin: 0 }}>
            {room.tenantName || 'Chambre libre'}
          </p>
        </div>

        <div className="rooms-actions">
          <button className="btn-action btn-edit" onClick={() => openEdit(room)}>
            Modifier
          </button>
          {/* Une chambre qui a un historique (locataire même parti,
              candidature, bail) ne se supprime pas : elle se ferme. La
              supprimer emportait le dossier d'un ancien locataire. */}
          <button
            className="btn-action btn-secondary"
            disabled={fermetureLoading}
            onClick={() => basculerFermeture(room)}
          >
            {room.isClosed ? 'Rouvrir' : 'Fermer la chambre'}
          </button>
          {room.supprimable && (
            <button
              className="btn-action btn-danger"
              onClick={() => {
                setDeleteError(null);
                setDeleteRoom(room);
              }}
            >
              Supprimer
            </button>
          )}
        </div>
      </div>

      {error && <div className="rooms-error">{error}</div>}

                        <div className="room-detail">
                          <div className="room-detail-grid">
                            {/* Tenant info */}
                            {room.tenantName && (
                              <div className="detail-section">
                                <h4>Locataire</h4>
                                <div className="detail-item"><span>Nom</span><span>{room.tenantName}</span></div>
                                {room.leaseStartDate && (
                                  <div className="detail-item"><span>Début bail</span><span>{formatDate(room.leaseStartDate)}</span></div>
                                )}
                                {room.monthlyRent != null && (
                                  <div className="detail-item"><span>Loyer réel</span><span>{formatCurrency(room.monthlyRent)}</span></div>
                                )}
                                {room.chargesProvision != null && (
                                  <div className="detail-item"><span>Charges réelles</span><span>{formatCurrency(room.chargesProvision)}</span></div>
                                )}
                                {room.noticeExpectedExitDate && (
                                  <div className="detail-item"><span>Sortie prévue</span><span>{formatDate(room.noticeExpectedExitDate)}</span></div>
                                )}
                              </div>
                            )}

                            {/* Availability */}
                            <div className="detail-section">
                              <h4>Disponibilité</h4>
                              {room.availabilityDate ? (
                                <div className="detail-item"><span>Disponible à partir du</span><span>{formatDate(room.availabilityDate)}</span></div>
                              ) : (
                                <div className="detail-item"><span>Date</span><span>{room.status === 'available' ? 'Immédiatement' : 'Non définie'}</span></div>
                              )}
                              <div className="detail-item"><span>Candidatures</span><span>{room.enrollmentCandidatesCount}</span></div>
                            </div>

                            {/* Equipment */}
                            <div className="detail-section">
                              <h4>Équipements</h4>
                              <div className="room-amenities">
                                {room.hasFurnitures && <span className="amenity-tag">Meublée</span>}
                                {room.isTV && <span className="amenity-tag">TV</span>}
                                {room.isCloset && <span className="amenity-tag">Placard</span>}
                                {room.isDesk && <span className="amenity-tag">Bureau</span>}
                                {room.isLocker && <span className="amenity-tag">Casier</span>}
                                {room.bedSize && <span className="amenity-tag">Lit {room.bedSize}cm</span>}
                                {room.bathroomSharedWith != null && <span className="amenity-tag">SdB partagée ({room.bathroomSharedWith})</span>}
                              </div>
                            </div>

                            {/* Financial details */}
                            <div className="detail-section">
                              <h4>Détails financiers</h4>
                              {room.depositAmount != null && (
                                <div className="detail-item"><span>Dépôt de garantie</span><span>{formatCurrency(room.depositAmount)}</span></div>
                              )}
                              {room.agencyExpenses != null && (
                                <div className="detail-item"><span>Frais d'agence</span><span>{formatCurrency(room.agencyExpenses)}</span></div>
                              )}
                              {room.lotNumber && (
                                <div className="detail-item"><span>N° lot</span><span>{room.lotNumber}</span></div>
                              )}
                              {room.tantiemes != null && (
                                <div className="detail-item"><span>Tantièmes</span><span>{room.tantiemes}</span></div>
                              )}
                            </div>
                          </div>
                        </div>

      {/* Edit room modal */}
      {editRoom && (
        <div className="modal-overlay" onClick={() => setEditRoom(null)}>
          <div className="modal-content modal-wide" onClick={e => e.stopPropagation()}>
            <h3>Modifier la chambre</h3>
            {editError && <div className="rooms-error">{editError}</div>}

            <div className="modal-tabs">
              <button
                className={`modal-tab ${editTab === 'characteristics' ? 'active' : ''}`}
                onClick={() => setEditTab('characteristics')}
              >
                Caractéristiques
              </button>
              <button
                className={`modal-tab ${editTab === 'financial' ? 'active' : ''}`}
                onClick={() => setEditTab('financial')}
              >
                Financier
              </button>
              <button
                className={`modal-tab ${editTab === 'photos' ? 'active' : ''}`}
                onClick={() => setEditTab('photos')}
              >
                Photos
              </button>
            </div>

            <form onSubmit={handleEdit} className="rooms-form">
              {editTab === 'characteristics' && (
                <>
                  <div className="form-group">
                    <label>Nom<FieldPreview {...APERCUS_CHAMBRE.nom} /></label>
                    <input type="text" className="form-control" value={editName} onChange={e => setEditName(e.target.value)} required />
                  </div>
                  {editRoom?.slug && selectedColoc?.slug && (
                    <div className="form-group">
                      <label>URL publique</label>
                      <div className="url-display">
                        <a href={urlChambrePublique(selectedColoc.slug, editRoom.slug)} target="_blank" rel="noopener noreferrer">
                          {urlChambrePublique(selectedColoc.slug, editRoom.slug)}
                        </a>
                      </div>
                    </div>
                  )}
                  <div className="form-row">
                    <div className="form-group">
                      <label>Étage</label>
                      <input type="number" className="form-control" value={editFloor} onChange={e => setEditFloor(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Surface (m²)<FieldPreview {...APERCUS_CHAMBRE.sdb} /></label>
                      <input type="number" step="0.1" min="1" className="form-control" value={editSurface} onChange={e => setEditSurface(e.target.value)} required />
                    </div>
                    <div className="form-group">
                      <label>Taille lit (cm)</label>
                      <input type="number" className="form-control" value={editBedSize} onChange={e => setEditBedSize(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-row checkboxes">
                    <label><input type="checkbox" checked={editHasFurnitures} onChange={e => setEditHasFurnitures(e.target.checked)} /> Meublée</label>
                    <label><input type="checkbox" checked={editIsTV} onChange={e => setEditIsTV(e.target.checked)} /> TV</label>
                    <label><input type="checkbox" checked={editIsCloset} onChange={e => setEditIsCloset(e.target.checked)} /> Placard</label>
                    <label><input type="checkbox" checked={editIsDesk} onChange={e => setEditIsDesk(e.target.checked)} /> Bureau</label>
                    <label><input type="checkbox" checked={editIsLocker} onChange={e => setEditIsLocker(e.target.checked)} /> Casier</label>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                  <BathroomSharing id="edit-sdb" value={editBathroomShared === '' ? null : Number(editBathroomShared)}
                    onChange={(v) => setEditBathroomShared(String(v))} />
                </div>
                  </div>
                  <div className="form-group">
                    <label>Description<FieldPreview {...APERCUS_CHAMBRE.description} /></label>
                    <textarea className="form-control" rows={3} value={editIntroTxt} onChange={e => setEditIntroTxt(e.target.value)} />
                  </div>
                  {editRoom && editRoom.status !== 'occupied' && (
                    <div className="form-group">
                      <label>Disponible à partir du {editRoom.status === 'available' ? '(laisser vide si immédiat)' : ''}</label>
                      <input type="date" className="form-control" value={editAvailableStarting} onChange={e => setEditAvailableStarting(e.target.value)} />
                      {editRoom.status === 'notice' && !editAvailableStarting && editRoom.availabilityDate && (
                        <small className="text-muted">Par défaut : {new Date(editRoom.availabilityDate).toLocaleDateString()}</small>
                      )}
                    </div>
                  )}
                </>
              )}

              {editTab === 'financial' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{isRentControlled ? 'Loyer de base (HC, €)' : 'Loyer HC (€)'}<FieldPreview {...APERCUS_CHAMBRE.loyer} /></label>
                      <input type="number" step="0.01" className="form-control" value={editPriceNIC} onChange={e => setEditPriceNIC(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Charges (€)</label>
                      <input type="number" step="0.01" className="form-control" value={editCharges} onChange={e => setEditCharges(e.target.value)} />
                    </div>
                  </div>
                  {isRentControlled && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Complément de loyer (€)</label>
                        <input type="number" step="0.01" className="form-control" value={editRentSupplement} onChange={e => setEditRentSupplement(e.target.value)} />
                        <small style={{ color: '#666', display: 'block', marginTop: 4 }}>
                          Zone encadrée : montant pour caractéristiques exceptionnelles, affiché distinctement sur le site public.
                        </small>
                      </div>
                    </div>
                  )}
                  <div className="form-row">
                    <div className="form-group">
                      <label>Dépôt de garantie (€)</label>
                      <input type="number" step="0.01" className="form-control" value={editDeposit} onChange={e => setEditDeposit(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Frais d'agence (€)</label>
                      <input type="number" step="0.01" className="form-control" value={editAgencyExpenses} onChange={e => setEditAgencyExpenses(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>N° de chambre (interne)</label>
                      <input type="number" className="form-control" value={editRoomNumber} onChange={e => setEditRoomNumber(e.target.value)} />
                  <small className="form-hint">{AIDE_NUMERO}</small>
                    </div>
                    <div className="form-group">
                      <label>N° lot</label>
                      <input type="text" className="form-control" value={editLotNumber} onChange={e => setEditLotNumber(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Tantièmes</label>
                      <input type="number" className="form-control" value={editTantiemes} onChange={e => setEditTantiemes(e.target.value)} />
                  <small className="form-hint">{AIDE_TANTIEMES}</small>
                    </div>
                  </div>
                </>
              )}

              {editTab !== 'photos' && (
                <div className="modal-actions">
                  <button type="button" className="btn-action btn-secondary" onClick={() => setEditRoom(null)}>Annuler</button>
                  <button type="submit" className="btn-action btn-save" disabled={editLoading}>
                    {editLoading ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              )}
            </form>

            {editTab === 'photos' && (
              <div className="edit-photos-section">
                <PhotoManager entityType="room" entityId={editRoom.id} />
                <div className="modal-actions">
                  <button type="button" className="btn-action btn-secondary" onClick={() => setEditRoom(null)}>Fermer</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Delete confirmation modal */}
      {deleteRoom && (
        <div className="modal-overlay" onClick={() => setDeleteRoom(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Supprimer la chambre</h3>
            {deleteError && <div className="rooms-error">{deleteError}</div>}
            <p>Êtes-vous sûr de vouloir supprimer la chambre <strong>{deleteRoom.name}</strong> ?</p>
            <div className="modal-actions">
              <button type="button" className="btn-action btn-secondary" onClick={() => setDeleteRoom(null)}>Annuler</button>
              <button
                type="button"
                className="btn-action btn-danger"
                disabled={deleteLoading}
                onClick={handleDelete}
              >
                {deleteLoading ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomDetailPage;
